import Vue from "vue";
import { find } from "lodash";
import { users } from "@/api";
import {
	getUser,
	getDashboards,
	createUser,
	getOrganisation,
	getHospital,
	getListings,
	putUser,
	postDashboards,
	postListings,
	getGroups,
	postGroups,
	resendInvite,
	getInvites,
	getConfig,
	putConfig
} from "@lab/api/users.api";
import {
	assignUserToOrganisation,
	removeUserFromOrganisation
} from "@lab/api/organisations.api";
import {
	assignUserToHospital,
	removeUserFromHospital
} from "@lab/api/hospitals.api";
import * as session from "@/utils/session";
import makeListingMixin from "@lab/store/vuexListingMixin";
import { User, CreatedUser } from "@lab/types/users";

export default makeListingMixin(
	"user",
	users
)({
	mutations: {
		userAssignOrg(state, { userID, organisationID }) {
			Vue.set(state.userOrg, userID, organisationID);
		},
		userLoadSuccess(state, user: User) {
			if (!user) return;
			const { organisation, hospital, listings, dashboards } = user;
			// Add org details if we have it
			if (organisation) {
				user.organisationid = organisation.id;
				user.organisationname = organisation.name;
			}
			// Add hospital details if we have it
			if (hospital) {
				user.hospitalid = hospital.id;
				user.hospitalname = hospital.name;
			}

			if (listings) {
				user.listingids = listings.map((listing) => listing.id);
			}

			if (dashboards) {
				user.dashboardids = dashboards.map((dashboard) => dashboard.id);
			}

			Vue.set(state.byID, user.id, user);

			state.isFetching = false;
			state.errorMessage = null;
		}
	},
	actions: {
		async userLoad({ commit, getters }, { id, force }) {
			commit(`userLoadStart`);

			let user = getters.userGet(id);

			try {
				if (typeof user === "undefined" || force) {
					user = await getUser(id);
				}
			} catch (error) {
				commit(`userLoadFailure`, error.message);
				return;
			}

			// Has it's own try block so it is allowed to fail
			try {
				if (getters.isOrganistionUser(user)) {
					const organisation = await getOrganisation(id);
					user.organisation = organisation;

					const listings = await getListings(user.id);
					user.listings = listings;
				}

				if (getters.isHospitalUser(user)) {
					const hospital = await getHospital(id);
					user.hospital = hospital;

					const groups = await getGroups(id);
					user.groups = groups;
				}

				const dashboards = await getDashboards(id);
				user.dashboards = dashboards;

				const config = await getConfig(id);

				if (config) {
					const {
						calloutrequests,
						searchresultssort,
						searchresultsview,
						sitetourrequests,
						terms,
						userdefaultgroup,
						meetingsenabled
					} = config;
					user.calloutrequests = calloutrequests;
					user.meetingsenabled = meetingsenabled;
					user.searchresultsview = searchresultsview;
					user.searchresultssort = searchresultssort;
					user.sitetourrequests = sitetourrequests;
					user.terms = terms;
					user.userdefaultgroup = Number(userdefaultgroup);
					// setting the default state on the groups so we can use it in the display and sorting
					if (user.groups) {
						user.groups.forEach((group) => {
							group.default = group.id === user.userdefaultgroup;
						});
					}
				}
				user.config = config;
			} catch (error) {
				console.log(error, "<<< org get failuer");
			}

			commit(`userLoadSuccess`, user);
		},
		async userInsert({ commit, getters }, data) {
			// So we don't send the organisationid to the api.
			const {
				organisationid,
				hospitalid,
				dashboardids,
				listingids = [],
				..._user
			} = data;
				console.log("userInsert", _user);

			try {
				const { user } = await createUser(_user);

				if (getters.isOrganistionUser(user)) {
					const organisation = await assignUserToOrganisation(
						user.id,
						organisationid
					);
					// Include organisationid for storage in store.
					user.organisation = organisation;
				}

				if (getters.isHospitalUser(user)) {
					const hospital = await assignUserToHospital(
						user.id,
						hospitalid
					);
					user.hospital = hospital;

					/**
					 * @TODO: [DC-2750] create assignUsersToGroups functionality
					 */
				}

				// Deal with if a dashboard has been set
				await postDashboards(user.id, dashboardids);

				const addListings = [
					"editor",
					"vacancy-editor",
					"report"
				].includes(_user.role);
				if (addListings && listingids.length) {
					await postListings(user.id, addListings ? listingids : []);
				}
				commit(`userInsertSuccess`, user);

				return user;
			} catch (error) {
				// Just rethrowing error for the moment.
				// will set isFetching to be false here though
				console.log(error);
				throw error;
			}
		},
		async userUpdate({ commit, getters, rootGetters }, data) {
			const {
				organisationid,
				hospitalid,
				listingids,
				dashboardids,
				groupids,
				..._user
			} = data;
				console.log("userUpdate", _user);
			try {
				let user = await putUser(_user);
				// Deal with the organisation, if it's been set.
				if (getters.isOrganistionUser(user)) {
					if (typeof organisationid !== "undefined") {
						if (organisationid) {
							const organisation = await assignUserToOrganisation(
								_user.id,
								organisationid
							);
							user.organisation = organisation;
						} else {
							// Remove from old org.
							// This part is redundant as the UI does not allow for a user
							// NOT to have a organisation assigned
							const oldUser = getters.userGet(_user.id);
							if (oldUser.organisationid) {
								await removeUserFromOrganisation(
									_user.id,
									oldUser.organisation.id
								);
							}
						}
					}
					// Deal with listings
					if (listingids) {
						const addListings = [
							"editor",
							"vacancy-editor",
							"report"
						].includes(_user.role);
						await postListings(
							_user.id,
							addListings ? listingids : []
						);
					}
				}
				// Deal with the hospital if it's been set
				if (getters.isHospitalUser(user)) {
					if (typeof hospitalid !== "undefined") {
						if (hospitalid) {
							const hospital = await assignUserToHospital(
								_user.id,
								hospitalid
							);
							user.hospital = hospital;
						} else {
							// Remove from old org.
							// This part is redundant as the UI does not allow for a user
							// NOT to have a hospital assigned
							const oldUser = getters.userGet(_user.id);
							if (oldUser.hospitalid) {
								await removeUserFromHospital(
									_user.id,
									oldUser.hospitalid
								);
							}
						}
					}

					if (groupids) {
						const groups = await postGroups(_user.id, groupids);
						user.groups = groups;
					}
				}

				if (dashboardids) {
					const dashboards = await postDashboards(
						_user.id,
						dashboardids
					);
					user.dashboards = dashboards;
				}

				if (
					_user.searchresultssort ||
					_user.searchresultsview ||
					_user.terms ||
					_user.meetingsenabled
				) {
					const config = {
						calloutrequests: _user.calloutrequests,
						searchresultssort: _user.searchresultssort,
						searchresultsview: _user.searchresultsview,
						sitetourrequests: _user.sitetourrequests,
						terms: _user.terms,
						userdefaultgroup: Number(_user.userdefaultgroup),
						meetingsenabled: _user.meetingsenabled
					};
					try {
						await putConfig(_user.id, config);
					} catch {}
				}

				commit(`userUpdateSuccess`, data);
				// Update the local storage IF it ME!
				user = rootGetters["userModule/get"];
				if (user.id === data.id) {
					session.saveUser({
						...user,
						...data
					});
				}
			} catch (error) {
				// Just rethrowing error for the moment.
				console.log(error);
				throw error;
			}
		},
		resendInvite(context, { id }) {
			resendInvite(id);
		},
		async getInvites(context, { id }) {
			await getInvites(id);
		}
	},
	getters: {
		// eslint-disable-next-line
		usersSuper(state, getters) {
			return getters.usersList.filter(getters.isSuperUser);
		},
		// eslint-disable-next-line
		usersAccessCustomer(state, getters) {
			return getters.usersList.filter(getters.isOrganistionUser);
		},
		// eslint-disable-next-line
		usersHospital(state, getters) {
			return getters.usersList.filter(getters.isHospitalUser);
		},
		// eslint-disable-next-line
		isSuperUser: (state) => (user) => {
			return user.role === "super";
		},
		// eslint-disable-next-line
		isOrganistionUser: (state, getters) => (user) => {
			return !!find(getters.listingUserRoles, {
				type: user.role
			});
		},
		// eslint-disable-next-line
		isHospitalUser: (state, getters) => (user) => {
			return !!find(getters.hospitalUserRoles, {
				type: user.role
			});
		},
		isDailyDataUser: (state, getters) => (user) => {
			return !!find(getters.dailyDataUserRoles, {
				type: user.role
			});
		},
		isDailyCareUser: (state, getters) => (user) => {
			return !!find(getters.dailyCareUserRoles, {
				type: user.role
			});
		}
	}
});
